import type { ReleaseStage } from './release';

/**
 * What a player is told before they pay, and the version of it they accepted.
 *
 * ## Why the text lives in code
 *
 * Because it is a promise. "100% at the open alpha, then 130% at launch" is a
 * commitment this codebase has to be able to honour later, and the only way to
 * know what was promised to whom is to store WHICH wording each order accepted.
 * The version goes on the order; changing a word means a new version, never an
 * edit.
 *
 * ## The version carries the STAGE
 *
 * `closed-alpha-terms-1`, `open-alpha-terms-1`, `official-terms-1`. The three
 * stages promise different things - one reset, one reset, or none - so an
 * acceptance given in the closed alpha must not silently authorise a purchase
 * made after the reset it was warning about. Changing stage therefore
 * invalidates every open confirmation, by construction rather than by a check
 * somebody has to remember.
 *
 * ## One source, several screens
 *
 * Every string here is rendered by the interface and by nothing else: the panel
 * composes no sentence of its own about the reset, the discount or the
 * recredit. A second copy in a component is a copy that eventually says
 * something no order records having promised.
 */

/**
 * The wording version for a stage.
 *
 * Bump the suffix on ANY change to that stage's text. Never edit a released
 * version's wording: an order that accepted `closed-alpha-terms-1` must stay
 * explicable by reading that exact string.
 *
 * `alpha-terms-1` and `alpha-terms-2` were the single-reset wordings, from
 * before the open alpha existed. Orders that accepted them keep saying so.
 */
export const RELEASE_TERMS_VERSIONS: Readonly<Record<ReleaseStage, string>> = Object.freeze({
  closed_alpha: 'closed-alpha-terms-1',
  open_alpha: 'open-alpha-terms-1',
  official: 'official-terms-1',
});

export function termsVersionFor(stage: ReleaseStage): string {
  return RELEASE_TERMS_VERSIONS[stage];
}

/**
 * Everything the reset erases, named one by one.
 *
 * A list rather than a paragraph, because "todo o progresso" is the kind of
 * phrase a player reads past. Somebody who owns a Prismatic cannon should find
 * the word "canhões" and understand that it means theirs.
 *
 * VIP is deliberately NOT here any more. A purchased VIP window is paid time,
 * not character progress, and it survives both resets - see
 * {@link RELEASE_VIP_NOTE}.
 */
export const ALPHA_RESET_WIPED: readonly string[] = Object.freeze([
  'Nível, XP e pontos distribuídos',
  'Atributos e progresso de mapas',
  'Gold',
  'Saldo atual de Cash',
  'Inventário completo',
  'Naves e canhões',
  'Artefatos',
  'Pets e skins',
  'Consumíveis e itens',
  'Anúncios no mercado',
  'Negociações e trades',
  'Progresso e histórico do gacha',
]);

/** VIP is paid TIME. It is the one thing a reset does not take. */
export const RELEASE_VIP_NOTE =
  'O tempo restante de VIP comprado continua válido através dos resetes. É um direito pago com duração real, não progresso do personagem.';

/** Shown where a promotion is expected and no deadline has been decided. */
export const ALPHA_PROMOTION_UNDATED = 'A data da promoção do Alpha ainda não foi definida.';

/**
 * The sentence that replaced "os pacotes e preços abaixo são os definitivos".
 *
 * That claim contradicted the project's own policy - the catalogue is expected
 * to move during the alphas - and a shop that says "definitive" and then
 * changes is a shop nobody has a reason to believe twice. What follows is both
 * halves of the truth: things may change, and a change never reaches back into
 * something already bought.
 */
export const STORE_CHANGES_NOTICE =
  'Os pacotes, preços, benefícios e condições promocionais poderão sofrer alterações durante os ' +
  'Alphas. Alterações futuras não modificam compras já concluídas nem o direito de recrédito do ' +
  'Cash elegível adquirido.';

/**
 * The two numbers, side by side, and the sentence that stops them being added.
 *
 * They are different things at different moments: half price is what you pay
 * TODAY, and the 30% bonus is what comes back at LAUNCH. Somebody adding them
 * up would read "150%", which is not a thing this store has ever offered - so
 * the refusal is written down rather than left to the layout.
 */
export const ALPHA_DISCOUNT_HEADLINE = 'AGORA: 50% de desconto';
export const ALPHA_DISCOUNT_DETAIL =
  'Durante a promoção do Alpha, todos os pacotes custam metade do preço normal.';
export const ALPHA_RECREDIT_HEADLINE = 'NO LANÇAMENTO: +30% de Cash';
export const ALPHA_RECREDIT_DETAIL =
  'O Cash elegível comprado nos Alphas volta acrescido de 30% para o comprador original: 1.000 Cash comprados viram 1.300 Cash na versão oficial.';
export const ALPHA_NOT_150 =
  'São dois benefícios distintos, em momentos diferentes: o desconto de 50% é sobre o preço pago agora, e o bônus de 30% é sobre o Cash recreditado no lançamento. Eles não se somam e isto não é um recredito de 150%.';

/** What a stage tells a player before they pay. */
export interface ReleaseNotice {
  readonly stage: ReleaseStage;
  readonly termsVersion: string;
  /** The loud line at the top of the shop. */
  readonly headline: string;
  /** One sentence, for where there is no room for the whole thing. */
  readonly summary: string;
  /** The stage's own text, in the order it must be read. */
  readonly paragraphs: readonly string[];
  /** The sentence a player has to tick before anything is charged. */
  readonly checkbox: string;
  /** Does this stage end in a reset? False only for `official`. */
  readonly resetsAtEnd: boolean;
}

const CLOSED_ALPHA_NOTICE: ReleaseNotice = Object.freeze({
  stage: 'closed_alpha',
  termsVersion: RELEASE_TERMS_VERSIONS.closed_alpha,
  headline: 'ALPHA FECHADO — TODO O PROGRESSO SERÁ RESETADO PARA O ALPHA ABERTO',
  summary:
    'Na abertura do Alpha Aberto, tudo o que existe nesta conta será apagado. O Cash elegível ' +
    'comprado diretamente na loja volta em 100% ao comprador original, e no lançamento oficial ' +
    'volta de novo com 30% adicional.',
  paragraphs: Object.freeze([
    'Na abertura do Alpha Aberto, níveis, XP, Gold, itens, inventário, naves, canhões, artefatos, pets, mapas, mercado, gacha e demais recursos de progresso serão apagados.',
    'O Cash elegível comprado diretamente na loja será devolvido em 100% ao comprador original no Alpha Aberto.',
    'No lançamento oficial, todo o Cash elegível comprado diretamente durante o Alpha Fechado e o Alpha Aberto será creditado novamente ao comprador original com 30% adicional.',
    'Cash recebido por trade, mercado, recompensa, concessão administrativa, sandbox ou outras fontes não conta. O tempo restante de VIP comprado continuará válido.',
  ]),
  checkbox:
    'Li e aceito que todo o progresso do Alpha Fechado será apagado na abertura do Alpha Aberto, ' +
    'que somente o Cash elegível comprado diretamente na loja será devolvido em 100% ao comprador ' +
    'original, e que no lançamento oficial esse Cash será creditado novamente com 30% adicional, ' +
    'conforme estas regras.',
  resetsAtEnd: true,
});

const OPEN_ALPHA_NOTICE: ReleaseNotice = Object.freeze({
  stage: 'open_alpha',
  termsVersion: RELEASE_TERMS_VERSIONS.open_alpha,
  headline: 'ALPHA ABERTO — TODO O PROGRESSO SERÁ RESETADO NO LANÇAMENTO OFICIAL',
  summary:
    'No lançamento oficial, tudo o que existe nesta conta será apagado. Todo o Cash elegível ' +
    'comprado diretamente nos dois Alphas volta ao comprador original com 30% adicional.',
  paragraphs: Object.freeze([
    'No lançamento oficial, níveis, XP, Gold, itens, inventário, naves, canhões, artefatos, pets, mapas, mercado, gacha e demais recursos de progresso serão apagados.',
    'Todo o Cash elegível comprado diretamente durante os dois Alphas será creditado ao comprador original com 30% adicional.',
    'Cash recebido por trade, mercado, recompensa, concessão administrativa, sandbox ou outras fontes não conta. O tempo restante de VIP comprado continuará válido.',
  ]),
  checkbox:
    'Li e aceito que todo o progresso do Alpha Aberto será apagado no lançamento oficial, e que ' +
    'somente o Cash elegível comprado diretamente na loja durante os Alphas será creditado ao ' +
    'comprador original com 30% adicional, conforme estas regras.',
  resetsAtEnd: true,
});

const OFFICIAL_NOTICE: ReleaseNotice = Object.freeze({
  stage: 'official',
  termsVersion: RELEASE_TERMS_VERSIONS.official,
  headline: 'VERSÃO OFICIAL — ECONOMIA PERMANENTE',
  summary:
    'Não há reset anunciado. O Cash comprado, os itens e o progresso desta conta permanecem.',
  paragraphs: Object.freeze([
    'A versão oficial não tem reset anunciado. O progresso, os itens e o Cash desta conta permanecem.',
    'Os preços exibidos são os normais. A promoção dos Alphas foi encerrada e não se aplica aqui.',
    'Cash é moeda virtual de uso exclusivo no jogo. Não tem valor monetário, não pode ser sacado e não representa investimento, rendimento ou promessa de retorno financeiro.',
    'O tempo restante de VIP comprado durante os Alphas continua válido nesta versão.',
  ]),
  checkbox:
    'Li e aceito que o Cash é moeda virtual de uso exclusivo no jogo, sem valor monetário, sem ' +
    'saque e sem promessa de retorno financeiro, conforme estas regras.',
  resetsAtEnd: false,
});

const NOTICES: Readonly<Record<ReleaseStage, ReleaseNotice>> = Object.freeze({
  closed_alpha: CLOSED_ALPHA_NOTICE,
  open_alpha: OPEN_ALPHA_NOTICE,
  official: OFFICIAL_NOTICE,
});

/** The notice for a stage. The ONE place an interface reads its wording from. */
export function releaseNoticeFor(stage: ReleaseStage): ReleaseNotice {
  return NOTICES[stage];
}

/**
 * The rules every stage shares, whatever it promises about a reset.
 *
 * What counts, what does not, and what Cash is. Shown under the stage's own
 * paragraphs rather than duplicated into each of them.
 */
export const RELEASE_COMMON_RULES: readonly string[] = Object.freeze([
  'Somente o Cash comprado diretamente na loja Kenocyber, com pagamento aprovado e benefício entregue, é elegível ao recredito.',
  'Vale a quantidade de Cash concedida pelo produto, não o saldo atual. Gastar, transferir ou usar o Cash no gacha não reduz o direito do comprador original.',
  'Cash recebido por trade, mercado, presente de outro jogador, recompensa, compensação ou concessão administrativa não conta.',
  'Pagamentos de sandbox, produtos internos de validação e compras reembolsadas, contestadas ou estornadas não contam.',
  'O próprio Cash devolvido em um reset não é uma nova compra e não entra novamente na base do recredito seguinte.',
  'A compra de VIP não gera recredito de Cash. Gold nunca entra no recredito.',
  'Cash é moeda virtual de uso exclusivo no jogo. Não tem valor monetário, não pode ser sacado e não representa investimento, rendimento ou promessa de retorno financeiro.',
]);

/** The button that opens the whole thing, worded the same everywhere. */
export const ALPHA_TERMS_LINK_LABEL = 'LER TODAS AS REGRAS';

/** The heading of the full rules. */
export const ALPHA_TERMS_TITLE = 'REGRAS DA FASE ATUAL';

/** VIP is bought as time, and it survives what a reset erases. */
export const ALPHA_TERMS_VIP_NOTE =
  'O VIP começa imediatamente após a aprovação do pagamento e corre continuamente. Ele não gera Cash elegível ao recredito, e o tempo restante continua válido depois de cada reset.';

/**
 * The consent kind a release-terms acceptance is recorded under.
 *
 * `consent_records` is append-only and already carries a version per record,
 * so the stage's own version is what distinguishes one acceptance from
 * another. Nothing is ever edited or deleted: an old acceptance stays exactly
 * as it was recorded, and simply does not answer for a version it was not
 * given for.
 */
export const RELEASE_TERMS_CONSENT_KIND = 'release_terms' as const;
